import { Address, ContactFile, ContactMessage, Partner } from '../../_generated';
import { ACUISITION_TYPES_ANGESTELLT, ACUISITION_TYPES_SELBSTSTAENDIG, AnstellungEnum } from '../models/partner.models';

/**
 * Utility functions for handling partner-related operations
 */
export const PartnerUtils = {
    /**
     * Generate a payload for updating a partner's name. This payload structure is
     * specifically formatted for SAP integration via Service Bus messaging.
     *
     * The message format and field names must
     * match SAP's expected schema for name change requests.
     */
    generateUpdateNamePayload: (firstName: string, lastName: string, policyNumber: string, fileContents: ContactFile[]) => {
        return {
            policennummer: policyNumber,
            reason: ContactMessage.ReasonEnum.NAME,
            message: { message: `Vorname: ${firstName} - Nachname: ${lastName}` },
            files: fileContents,
        } as ContactMessage;
    },

    /**
     * Generate a payload for updating a partner's civil status. This payload structure is
     * specifically formatted for SAP integration via Service Bus messaging.
     *
     * The message format and field names must
     * match SAP's expected schema for name change requests.
     */
    generateUpdateCivilStatusPayload: (policyNumber: string, civilStatus: string) => {
        // TODO: The key 'Zivilstand' might need to be translated for FR and IT when sending to SAP. Check on TST.
        return {
            policennummer: policyNumber,
            reason: ContactMessage.ReasonEnum.ZIVILSTAND,
            message: { message: `Zivilstand: ${civilStatus}` },
            files: [],
        } as ContactMessage;
    },

    /**
     * Generate a payload for updating a partner. This payload structure is
     * specifically formatted for SAP integration via Service Bus messaging.
     *
     * NOTE:
     * This is designed to work exactly like the old KuPo so the communication with SAP still works as intended.
     * This is why the code might seem a bit weird.
     *
     * The message format and field names must
     * match SAP's expected schema for name change requests.
     */

    generateUpdatePartnerPayload: (
        partner: Partner | null,
        updatedFields: {
            adresseGueltigab?: string;
            adresseStrasse?: string;
            adresseHausnummer?: string;
            adressePostleitzahl?: string;
            adresseOrt?: string;
            adresseLand?: string;
            email?: string;
            telefon?: string;
            erwerbsart?: string;
            profession?: string;
            sozialversicherungsnummer?: string;
            zivilstand?: string;
        },
    ) => {
        if (!partner) return null;
        const payload = {
            partnernummer: partner.partnernummer,
            reason: ContactMessage.ReasonEnum.PARTNER_AENDERUNG_PV,
            message: {
                message: '',
                updatedFields: [
                    {
                        field: 'adresseGueltigab',
                        value: updatedFields.adresseGueltigab || partner.adresseGueltigab,
                        isChanged: updatedFields.adresseGueltigab !== undefined && updatedFields.adresseGueltigab !== partner.adresseGueltigab,
                    },
                    {
                        field: 'adresseStrasse',
                        value: updatedFields.adresseStrasse || partner.adresseStrasse,
                        isChanged: updatedFields.adresseStrasse !== undefined && updatedFields.adresseStrasse !== partner.adresseStrasse,
                    },
                    {
                        field: 'adresseHausnummer',
                        value: updatedFields.adresseHausnummer || partner.adresseHausnummer,
                        isChanged: updatedFields.adresseHausnummer !== undefined && updatedFields.adresseHausnummer !== partner.adresseHausnummer,
                    },
                    {
                        field: 'adressePostleitzahl',
                        value: updatedFields.adressePostleitzahl || partner.adressePostleitzahl,
                        isChanged: updatedFields.adressePostleitzahl !== undefined && updatedFields.adressePostleitzahl !== partner.adressePostleitzahl,
                    },
                    {
                        field: 'adresseOrt',
                        value: updatedFields.adresseOrt || partner.adresseOrt,
                        isChanged: updatedFields.adresseOrt !== undefined && updatedFields.adresseOrt !== partner.adresseOrt,
                    },
                    {
                        field: 'adresseLand',
                        value: updatedFields.adresseLand || partner.adresseLand,
                        isChanged: updatedFields.adresseLand !== undefined && updatedFields.adresseLand !== partner.adresseLand,
                    },
                    {
                        field: 'email',
                        value: updatedFields.email || partner.email,
                        isChanged: updatedFields.email !== undefined && updatedFields.email !== partner.email,
                    },
                    {
                        field: 'telefon',
                        value: updatedFields.telefon || partner.telefon,
                        isChanged: updatedFields.telefon !== undefined && updatedFields.telefon !== partner.telefon,
                    },
                    {
                        field: 'erwerbsart',
                        value: updatedFields.erwerbsart || partner.erwerbsart,
                        isChanged: updatedFields.erwerbsart !== undefined && updatedFields.erwerbsart !== partner.erwerbsart,
                    },
                    {
                        field: 'profession',
                        value: updatedFields.profession || partner.beruf,
                        isChanged: updatedFields.profession !== undefined && updatedFields.profession !== partner.beruf,
                    },
                    {
                        field: 'sozialversicherungsnummer',
                        value: updatedFields.sozialversicherungsnummer || partner.sozialversicherungsnummer,
                        isChanged: updatedFields.sozialversicherungsnummer !== undefined && updatedFields.sozialversicherungsnummer !== partner.sozialversicherungsnummer,
                    },
                    {
                        field: 'zivilstand',
                        value: updatedFields.zivilstand || partner.zivilstand,
                        isChanged: updatedFields.zivilstand !== undefined && updatedFields.zivilstand !== partner.zivilstand,
                    },
                    // TODO: The following 3 are always empty because there is no way to change these values in the FE. Maybe SAP still needs them.
                    /* {
                        field: 'adresseZusatz',
                        value: '',
                        isChanged: false,
                    },
                    {
                        field: 'adresseCo',
                        value: '',
                        isChanged: false,
                    },
                    {
                        field: 'adressePostfach',
                        value: '',
                        isChanged: false,
                    }, */
                ],
            },
            files: [],
        };

        let message = '';
        payload.message.updatedFields.forEach((entry, i) => {
            message += `"${entry.field}":"${entry.value}"`;
            message += i < payload.message.updatedFields.length - 1 ? ',' : '';
        });
        payload.message.message = `{${message}}`;
        return payload;
    },

    getFullName: (partner: Partner) => {
        return `${partner.vorname} ${partner.nachname}`;
    },

    getFullAddress: (partner: Partner) => {
        return `${partner.adresseStrasse} ${partner.adresseHausnummer}, ${partner.adresseLand}-${partner.adressePostleitzahl} ${partner.adresseOrt}`;
    },

    getFormattedAddress: (address: Address) => {
        const streetPart = [address.strasse.trim(), address.hausnummer.trim()].filter((val) => val !== '').join(' ');

        const locationPart = [address.postleitzahl.trim(), address.ort.trim()].filter((val) => val !== '').join(' ');

        return [streetPart, locationPart].filter((val) => val !== '').join(', ');
    },

    getMax3aValue(erwerbsart: string, max3aValues: { max3aAngestellt: number; max3aSelbststaendig: number }): number | null {
        const anstellung = this.getAnstellung(erwerbsart);
        if (anstellung === AnstellungEnum.Angestellt) return Number(max3aValues.max3aAngestellt);
        if (anstellung === AnstellungEnum.Selbststaendig) return Number(max3aValues.max3aSelbststaendig);
        return null;
    },

    getAnstellung(erwerbsart: string): AnstellungEnum {
        if (ACUISITION_TYPES_ANGESTELLT.includes(erwerbsart)) {
            return AnstellungEnum.Angestellt;
        }
        if (ACUISITION_TYPES_SELBSTSTAENDIG.includes(erwerbsart)) {
            return AnstellungEnum.Selbststaendig;
        }
        return AnstellungEnum.OhneAnstellung;
    },
};
